import { produce } from 'immer';
import { useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  hasValidationErrors,
  setValidationErrors,
  showErrorToast,
} from '../../../../../common/global/helpers';
import useBreakpoints from '../../../../../common/hooks/useBreakpoints';
import useMount from '../../../../../common/hooks/useMount';
import Breadcrumbs from '../../../../../common/widgets/Breadcrumbs';
import Button from '../../../../../common/widgets/Button';
import Dialog from '../../../../../common/widgets/Dialog';
import Dropdown from '../../../../../common/widgets/Dropdown';
import Icons from '../../../../../common/widgets/Icons';
import Input from '../../../../../common/widgets/Input';
import Loader from '../../../../../common/widgets/Loader';
import LoadingSpinner from '../../../../../common/widgets/LoadingSpinner';
import RadioGroup from '../../../../../common/widgets/RadioGroup';
import TextArea from '../../../../../common/widgets/TextArea';
import { CustomerInfoContext } from '../../../contexts/CustomerInfoContext';
import { DropdownOptionModel } from '../../../models/Common';
import { ErrorState, ErrorType } from '../../../models/Errors';
import { resources } from '../../../resources/resources';
import { helpService } from '../../../services/help';
import applicationCrumbs from '../../ApplicationCrumbs';
import { HelpMenu } from '../HelpMenu';

interface ContactPageState {
  showConfirmationDialog?: boolean;
  isSendButtonLoading?: boolean;
  details?: {
    contactReasons: DropdownOptionModel[];
    unhelpfulAnswerReasons: DropdownOptionModel[];
  };
  form: {
    message?: string;
    contactReason?: DropdownOptionModel;
    unhelpfulAnswerReasonId?: number;
  };
  showErrorPanel?: boolean;
}

export const ContactPage = () => {
  const { t: translate } = useTranslation();
  const crumbs = applicationCrumbs(translate);
  const [state, setState] = useState<ContactPageState>({ form: {} });
  const [errors, setErrors] = useState<ErrorState>({});
  const { customerInfo } = useContext(CustomerInfoContext);
  const { isTablet } = useBreakpoints();
  const navigate = useNavigate();

  const loadFormDetails = async (abortController?: AbortController) => {
    const resp = await helpService.getContactFormDetails({
      signal: abortController?.signal,
    });
    if (!resp.hasErrors) {
      setState(
        produce(d => {
          d.details = resp.data;
          d.form.contactReason = resp.data.contactReasons[0];
          d.form.unhelpfulAnswerReasonId =
            resp.data.unhelpfulAnswerReasons[0].value;
        }),
      );
    } else {
      if (!abortController?.signal.aborted) {
        setState(
          produce(d => {
            d.showErrorPanel = true;
          }),
        );
      }
    }
  };

  useMount(loadFormDetails);

  const validateData = (): ErrorState => {
    const newErrors: ErrorState = {};
    if (!state.form.message) {
      newErrors.message = [
        {
          target: 'message',
          code: resources.validation.required,
          severity: 'error',
          placeholders: {},
        },
      ];
    }
    setErrors(newErrors);
    return newErrors;
  };

  const onSendClick = async () => {
    const newErrors = validateData();
    if (!hasValidationErrors(newErrors)) {
      setState(
        produce(d => {
          d.isSendButtonLoading = true;
        }),
      );
      const resp = await helpService.contact({
        contactReason: state.form.contactReason!.text,
        unhelpfulAnswerReason: state.details!.unhelpfulAnswerReasons.find(
          e => e.value === state.form.unhelpfulAnswerReasonId,
        )!.text,
        message: state.form.message!,
      });
      setState(
        produce(d => {
          d.isSendButtonLoading = false;
        }),
      );
      if (!resp.hasErrors) {
        setState(
          produce(d => {
            d.showConfirmationDialog = true;
          }),
        );
      } else {
        if (resp.errorType === ErrorType.ValidationError) {
          setValidationErrors(resp, setErrors);
        } else {
          showErrorToast(translate(resources.errors.cannotSendEmail));
        }
      }
    }
  };

  const clearErrors = (name: string) => {
    setErrors(
      produce(d => {
        if (d) {
          d[name] = [];
        }
      }),
    );
  };

  return (
    <>
      <Breadcrumbs entries={[crumbs.home, crumbs.help]} />
      <section className="section-main section-actions help-section-page contact-page">
        <div className="container">
          <div className="area-actions">
            <div className="action-panel">
              <div className="action-panel-content secondary-nav help-nav">
                <HelpMenu />
              </div>
            </div>
          </div>
          <div className="area-content">
            <div className="panel contact-panel padding-xl mb-60">
              <h1 className="title-back-btn">
                {isTablet && (
                  <a
                    onClick={() => navigate(-1)}
                    className="btn btn-black-mobile"
                  >
                    <span className="btn-icon">
                      <Icons.ArrowBackMobile />
                    </span>
                  </a>
                )}
                {translate(resources.help.contact.title)}
              </h1>
              <Loader
                loading={!state.details}
                loader={<LoadingSpinner />}
                showErrorPanel={state.showErrorPanel}
              >
                {!!state.details && (
                  <>
                    <Input
                      type="text"
                      disabled
                      label={translate(resources.help.contact.name)}
                      name={translate(resources.help.contact.name)}
                      title={translate(resources.help.contact.name)}
                      value={customerInfo.fullName}
                    />
                    <Input
                      type="text"
                      disabled
                      label={translate(resources.help.contact.email)}
                      name={translate(resources.help.contact.email)}
                      title={translate(resources.help.contact.email)}
                      value={customerInfo.email}
                    />

                    <Dropdown
                      label={translate(resources.help.contact.reason)}
                      dataItemKey="value"
                      textField="text"
                      data={state.details.contactReasons}
                      value={state.form.contactReason}
                      onChange={e =>
                        setState(
                          produce(d => {
                            d.form.contactReason = e.value;
                          }),
                        )
                      }
                      mandatory
                    />
                    <RadioGroup
                      label={translate(
                        resources.help.contact.unhelpfulAnswerReason,
                      )}
                      value={state.form.unhelpfulAnswerReasonId}
                      onChange={e =>
                        setState(
                          produce(d => {
                            d.form.unhelpfulAnswerReasonId = e.value;
                          }),
                        )
                      }
                      data={state.details.unhelpfulAnswerReasons.map(e => ({
                        label: e.text,
                        value: e.value,
                      }))}
                    />
                    <TextArea
                      label={translate(resources.help.contact.message)}
                      value={state.form.message}
                      onChange={e =>
                        setState(
                          produce(d => {
                            d.form.message = e.value;
                          }),
                        )
                      }
                      mandatory
                      errors={errors.message}
                      clearErrors={() => clearErrors('message')}
                      helper={translate(resources.help.contact.messageInfo)}
                    />
                    <div className="contact-action">
                      <Button
                        className="btn btn-solid btn-lg"
                        onClick={onSendClick}
                        loading={state.isSendButtonLoading}
                      >
                        {translate(resources.help.contact.send)}
                      </Button>
                    </div>
                    {state.showConfirmationDialog && <ConfirmationDialog />}
                  </>
                )}
              </Loader>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

const ConfirmationDialog = () => {
  const navigate = useNavigate();
  const { t: translate } = useTranslation();
  return (
    <Dialog
      closeDialog={() => navigate('/help')}
      closeOnClickOutside
      showCloseIcon
      className="dialog-s"
    >
      <div className="dialog-content feedback-dialog">
        <div className="dialog-image">
          <Icons.Success />
        </div>
        <div className="dialog-title">
          {translate(resources.help.contact.confirmation.title)}
        </div>
        <div className="dialog-text centered">
          {translate(resources.help.contact.confirmation.message)}
        </div>
      </div>
    </Dialog>
  );
};
